import { fetchCashOutAccounts, processCashOut } from '@/lib/api/income';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import { stanNotify } from '@/lib/stanNotify';
import { useBalance } from '@/queries/useBalance';
import { useUser } from '@/queries/useUser';
import { formatCurrency } from '@/lib/currency';
import { useEffect, useState } from 'react';

// Helper function to join array items with commas and 'and'
function joinWithCommasAnd(items: string[]): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0] || '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    const lastItem = items[items.length - 1];
    return `${items.slice(0, -1).join(', ')}, and ${lastItem}`;
}

export function useCashout({
    onClose,
    onSuccess,
    payoutSchedule,
}: {
    onClose: () => void;
    onSuccess?: (amount: number, currency: string) => void;
    payoutSchedule?: number;
}) {
    const queryClient = useQueryClient();
    const { balance, refetchBalance } = useBalance();
    const { user } = useUser();
    const [bankAccounts, setBankAccounts] = useState<string[]>([]);
    const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

    const loadCashoutInfo = async () => {
        setIsLoadingAccounts(true);
        try {
            const cashOutAccounts = await fetchCashOutAccounts();
            const accountNames = Object.values(cashOutAccounts.accounts)
                .map(ba => ba.readable_name)
                .filter((name): name is string => name !== undefined);
            setBankAccounts(accountNames);
        } catch (error) {
            const message =
                isAxiosError(error) && error.response?.data?.message
                    ? error.response.data.message
                    : 'We could not verify your bank account';

            stanNotify({
                type: 'error',
                title: 'Cash Out',
                text: message,
                duration: 5000,
                error: {
                    message: '[INCOME] failed-to-fetch-bank-accounts',
                    options: {
                        tags: { feature: SENTRY_FEATURES.INCOME },
                        context: { error },
                    },
                },
            });
        } finally {
            setIsLoadingAccounts(false);
        }
    };

    // Fetch bank accounts once on mount (matches Vue's mounted() behavior)
    useEffect(() => {
        loadCashoutInfo();
    }, []);

    const cashOutMutation = useMutation({
        mutationFn: processCashOut,
        onSuccess: () => {
            const amount = balance?.stripe_available?.amount || 0;
            const currency = balance?.stripe_available?.currency || 'USD';

            refetchBalance();
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDERS] });

            stanAnalytics('cash-out', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    cashout_amount: amount,
                    cashout_currency: currency,
                    instant_payout: !!balance?.stripe_available?.instant_payout,
                },
            });

            onClose();

            // Trigger celebration modal
            if (onSuccess) {
                onSuccess(amount, currency);
            }
        },
        onError: error => {
            const message =
                isAxiosError(error) && error.response?.data?.message
                    ? error.response.data.message
                    : 'We could not process this transaction. Please contact our support.';

            stanNotify({
                type: 'error',
                title: 'Cash Out',
                text: message,
                duration: 5000,
                error: {
                    message: '[INCOME] failed-to-cash-out',
                    options: {
                        tags: { feature: SENTRY_FEATURES.INCOME },
                        context: { error },
                    },
                },
            });
        },
    });
    const cashoutBalance = balance?.stripe_available
        ? balance.stripe_available.instant_payout
            ? formatCurrency(balance.stripe_available.instant_payout_max_amount || 0, balance.stripe_available.currency)
            : formatCurrency(balance.stripe_available.amount || 0, balance.stripe_available.currency)
        : '$0.00';

    const pendingBalance = balance?.stripe_pending
        ? formatCurrency(balance.stripe_pending.amount || 0, balance.stripe_pending.currency)
        : '$0.00';

    const holdTimeEstimate = payoutSchedule
        ? `They are expected to become available within ${payoutSchedule} days.`
        : 'They are expected to become available within 2-3 business days.';

    const depositString = bankAccounts.length > 0 ? `To be deposited into ${joinWithCommasAnd(bankAccounts)}` : '';

    const hasPendingBalance = balance?.stripe_pending && balance.stripe_pending.amount > 0;
    const hasAvailableBalance = balance?.stripe_available && balance.stripe_available.amount > 0;

    return {
        cashout: cashOutMutation.mutate,
        isProcessing: cashOutMutation.isPending || isLoadingAccounts,
        cashoutBalance,
        pendingBalance,
        holdTimeEstimate,
        depositString,
        hasPendingBalance,
        hasAvailableBalance,
        isLoadingAccounts,
    };
}
